/** Cálculos puros de geometria. Não dependem de Angular nem do DOM. */
export interface DialogGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DialogViewport {
  width: number;
  height: number;
}

export type DialogSnapZone =
  | 'left' | 'right'
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  | 'maximized';

export type DialogResizeEdge = 'top' | 'right' | 'bottom' | 'left'
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const DEFAULT_DIALOG_MIN_WIDTH = 320;
export const DEFAULT_DIALOG_MIN_HEIGHT = 180;

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function fitDialogToViewport(
  rect: DialogGeometry, viewport: DialogViewport,
  minWidth = DEFAULT_DIALOG_MIN_WIDTH, minHeight = DEFAULT_DIALOG_MIN_HEIGHT
): DialogGeometry {
  const vw = Math.max(1, viewport.width);
  const vh = Math.max(1, viewport.height);
  const lowerWidth = Math.min(minWidth, vw);
  const lowerHeight = Math.min(minHeight, vh);
  const width = clamp(Number.isFinite(rect.width) ? rect.width : lowerWidth, lowerWidth, vw);
  const height = clamp(Number.isFinite(rect.height) ? rect.height : lowerHeight, lowerHeight, vh);
  return {
    x: clamp(Number.isFinite(rect.x) ? rect.x : 0, 0, Math.max(0, vw - width)),
    y: clamp(Number.isFinite(rect.y) ? rect.y : 0, 0, Math.max(0, vh - height)),
    width, height
  };
}

export function geometryForSnap(zone: DialogSnapZone, viewport: DialogViewport): DialogGeometry {
  const w = Math.max(1, viewport.width);
  const h = Math.max(1, viewport.height);
  const leftW = Math.floor(w / 2);
  const topH = Math.floor(h / 2);
  switch (zone) {
    case 'left': return { x: 0, y: 0, width: leftW, height: h };
    case 'right': return { x: leftW, y: 0, width: w - leftW, height: h };
    case 'top-left': return { x: 0, y: 0, width: leftW, height: topH };
    case 'top-right': return { x: leftW, y: 0, width: w - leftW, height: topH };
    case 'bottom-left': return { x: 0, y: topH, width: leftW, height: h - topH };
    case 'bottom-right': return { x: leftW, y: topH, width: w - leftW, height: h - topH };
    case 'maximized': return { x: 0, y: 0, width: w, height: h };
  }
}

/** Coordenadas do ponteiro em relação à área visível do browser. */
export function detectDragSnap(
  x: number, y: number, viewport: DialogViewport,
  threshold = 28, cornerThreshold = 76
): DialogSnapZone | null {
  const { width: w, height: h } = viewport;
  if (x < 0 || y < 0 || x > w || y > h) return null;
  const left = x <= threshold;
  const right = x >= w - threshold;
  const top = y <= threshold;
  const bottom = y >= h - threshold;
  // As zonas dos cantos são mais generosas para facilitar apontar com o rato.
  if ((left || (top && x <= cornerThreshold)) && y <= cornerThreshold) return 'top-left';
  if ((right || (top && x >= w - cornerThreshold)) && y <= cornerThreshold) return 'top-right';
  if ((left || (bottom && x <= cornerThreshold)) && y >= h - cornerThreshold) return 'bottom-left';
  if ((right || (bottom && x >= w - cornerThreshold)) && y >= h - cornerThreshold) return 'bottom-right';
  if (top) return 'maximized';
  if (left) return 'left';
  if (right) return 'right';
  return null;
}

/** Limites visíveis da janela durante o drag, em coordenadas do viewport. */
export interface DialogDragBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Snap durante o drag de uma janela com cdkDragBoundary.
 *
 * O CDK impede que o retângulo da janela ultrapasse o viewport. Por isso,
 * o cursor (normalmente agarrado a meio da barra de título) pode continuar
 * longe da extremidade mesmo quando a janela já lhe está encostada.
 *
 * Consideramos ambos: cursor + arestas reais da janela. Isto permite
 * detetar as quatro zonas dos cantos e as duas laterais sem depender do
 * ponto exato em que o utilizador agarrou a janela.
 */
export function detectDialogDragSnap(
  pointerX: number,
  pointerY: number,
  bounds: DialogDragBounds,
  viewport: DialogViewport,
  edgeThreshold = 16,
  pointerThreshold = 28
): DialogSnapZone | null {
  const w = Math.max(1, viewport.width);
  const h = Math.max(1, viewport.height);
  const leftByWindow = bounds.left <= edgeThreshold;
  const rightByWindow = bounds.right >= w - edgeThreshold;
  const topByWindow = bounds.top <= edgeThreshold;
  const bottomByWindow = bounds.bottom >= h - edgeThreshold;

  let left = leftByWindow || pointerX <= pointerThreshold;
  let right = rightByWindow || pointerX >= w - pointerThreshold;
  let top = topByWindow || pointerY <= pointerThreshold;
  let bottom = bottomByWindow || pointerY >= h - pointerThreshold;

  // Quando uma janela ocupa toda (ou quase toda) a largura/altura,
  // as duas arestas podem estar sempre encostadas ao viewport. Nesses casos
  // o contacto do retângulo NÃO é suficiente para ativar um snap: exigir que
  // o ponteiro se aproxime efetivamente do limite pretendido. Caso contrário,
  // retirar um snap lateral (altura 100%) pode maximizar a janela sem intenção.
  if (left && right) {
    left = pointerX <= pointerThreshold;
    right = pointerX >= w - pointerThreshold;
  }
  if (top && bottom) {
    top = pointerY <= pointerThreshold;
    bottom = pointerY >= h - pointerThreshold;
  }

  if (left && top) return 'top-left';
  if (right && top) return 'top-right';
  if (left && bottom) return 'bottom-left';
  if (right && bottom) return 'bottom-right';
  if (top) return 'maximized';
  if (left) return 'left';
  if (right) return 'right';
  return null;
}

export function geometryForResize(
  initial: DialogGeometry, edge: DialogResizeEdge, dx: number, dy: number,
  viewport: DialogViewport, minWidth = DEFAULT_DIALOG_MIN_WIDTH, minHeight = DEFAULT_DIALOG_MIN_HEIGHT
): DialogGeometry {
  const x0 = initial.x;
  const y0 = initial.y;
  const right0 = initial.x + initial.width;
  const bottom0 = initial.y + initial.height;
  let left = x0;
  let top = y0;
  let right = right0;
  let bottom = bottom0;
  const minW = Math.min(minWidth, viewport.width);
  const minH = Math.min(minHeight, viewport.height);

  if (edge.includes('left')) left = clamp(x0 + dx, 0, right0 - minW);
  else if (edge.includes('right')) right = clamp(right0 + dx, left + minW, viewport.width);
  if (edge.includes('top')) top = clamp(y0 + dy, 0, bottom0 - minH);
  else if (edge.includes('bottom')) bottom = clamp(bottom0 + dy, top + minH, viewport.height);

  return {
    x: left, y: top,
    width: right - left, height: bottom - top
  };
}
